import type { CommentData, ContentEntity } from "shared/schema";
import { type AbsolutePath, type Item, pluralizeQuantity } from "shelving";
import { Ago, ModalButton, PathButton } from "shared/react";
import { getEntityPath, getMarkupExcerpt } from "shared/schema";
import { pluralizeCompactQuantity } from "shared/util/number";
import { EntityButton } from "../entity/EntityButton.js";
import { ReactionSummary } from "../reaction/ReactionSummary.js";
import { useItem, useQuery } from "../util/db.js";
import { CommentForm } from "./CommentForm.js";

export function Comment({ id, showReplies = false }: { id: string; showReplies?: boolean }): React.ReactElement {
	const comment = useItem("comment", id).data;
	const { content, replies, target } = comment;
	return (
		<article className="targeted">
			<CommentHeader {...comment} />
			<p className="space-xsmall">{getMarkupExcerpt(content)}</p>
			{showReplies ? <CommentReplies replies={replies} target={target} thread={id} /> : null}
		</article>
	);
}

export function CommentReplies({ replies, target, thread }: { replies: number; target: ContentEntity; thread: string | null }): React.ReactElement | null {
	const remaining = Math.max(replies - 1, 0);
	return (
		<blockquote className="space-xsmall">
			{remaining ? (
				<a className="link color-quiet" href={thread ? `/comment/${thread}` : getEntityPath(target)}>
					{pluralizeQuantity(remaining, "more reply", "more replies")}
				</a>
			) : null}
			<LatestComment target={target} thread={thread} />
		</blockquote>
	);
}

export function LatestComment({ target, thread }: { target: ContentEntity; thread: string | null }): React.ReactElement | null {
	const comment = useQuery("comment", {
		target,
		thread,
		$order: "!date",
		$limit: 1,
	}).optionalFirst;
	return comment ? <Comment id={comment.id} /> : null;
}

export function CommentHeader({ id, creator, date, replies, target, reactions }: Item<CommentData>): React.ReactElement {
	const entity: ContentEntity = `comment:${id}`;
	const path: AbsolutePath = `/comment/${id}`;
	return (
		<header className="quiet space-xsmall flex-row flex-left gap-xsmall">
			<EntityButton entity={creator} />
			<PathButton path={path} mods="button-quiet target">
				<Ago target={date} />
			</PathButton>
			<PathButton path={path} mods="button-quiet">
				{pluralizeCompactQuantity(replies, "reply", "replies")}
			</PathButton>
			<ModalButton modal={<CommentForm target={target} thread={null} />} mods="button-quiet">
				Reply
			</ModalButton>
			<ReactionSummary count={reactions} target={entity} />
		</header>
	);
}
